import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { uploadTransfer } from '../../../../transfer-bundle/store/actions';

import UploaderMessage from './uploader-message';
import TransferTypeSelector from './transfer-type-selector';
import UploaderRecipients from './recipients';
import Wizard from './wizard';
import Step from './step';

import Files from '@component/transfer/uploader/files';
import I18n from '@module/i18n';
import Actions from '@app/action';
import Helper from '@app/helper';
import { hasStarted } from '@app/transfer-bundle/helpers';
import { getCurrentSize } from '@app/transfer-bundle/store/selectors';
import { allowedSizeSelector } from '@app/selector/transfers';
import { getIsForwardedTransfer } from '@app/transfer-bundle/store/selectors';
import { EXPERIMENT_TRANSFER_VERIFICATION } from '@app/constant/experiments';
import LaunchDarkly from '@module/launchdarkly';
import { TRANSFER_STATES } from '@app/transfer-bundle/constant/transfer-states';

export class MobileUploader extends React.PureComponent {
  state = {
    senderEmail: this.props.transfer.sender,
    showDefaultRecipientMobileInfo: false,
    transferStarted: false,
    forceVerification: false,
  };

  componentDidMount() {
    const { user, setSender, transfer } = this.props;
    if (transfer.state === TRANSFER_STATES.DOWNLOADABLE) {
      this.cancelTransfer();
    }
    if (Helper.isSenderLocked(user.loggedIn)) {
      setSender(user.email);
      this.setState({ senderEmail: user.email });
    }
    if (
      !user.loggedIn &&
      LaunchDarkly.getVariant(EXPERIMENT_TRANSFER_VERIFICATION)
    ) {
      this.setState({ forceVerification: true });
    }
  }

  // To cancel a transfer just create a new empty one
  cancelTransfer = () => {
    this.props.create();
  };

  setMessage = (value) => {
    this.props.setMessage(value);
  };

  setTransferTypeEmail = () => {
    this.props.changeType('email');
  };

  setTransferTypeLink = () => {
    this.props.changeType('link');
  };

  onSenderChange = (event) => {
    event.preventDefault();

    this.setState({ senderEmail: event.target.value });
  };

  onSenderBlur = (event) => {
    this.props.setSender(event.target.value);
  };

  // checking if transfer can be started or not
  isTransferBlocked() {
    const { session, transfer } = this.props;
    const { transferStarted } = this.state;
    return (
      !session.tandc ||
      hasStarted(transfer.state, transfer.id) ||
      transferStarted
    );
  }

  startTransfer = () => {
    const {
      clearTransferNotification,
      uploadTransfer,
      transfer,
      channel,
      account,
      user,
      session,
      notification,
      allowedSize,
      isForwardedTransfer,
    } = this.props;

    if (this.isTransferBlocked()) {
      return false;
    }

    // update the state to prevent sending multiple transfers
    this.setState({ transferStarted: true }, () => {
      clearTransferNotification();

      uploadTransfer({
        channel,
        account,
        user,
        session,
        notification,
        allowedSize,
        currentSize: getCurrentSize({ transfer }),
        isForwardedTransfer,
        forceVerification: this.state.forceVerification,
      }).finally(() => {
        this.setState({ transferStarted: false });
      });
    });
  };

  render() {
    const { transfer, channel, user, account } = this.props;

    //if files are already in the transfer we skip first step
    const hasFiles = transfer.files.length > 0;

    const lockedRecipient = Helper.isDefaultRecipientInUploadLocked({
      channelDefaultRecipient: channel && channel.defaultRecipient,
      channelProfileReceivingLocked: channel && channel.profileReceivingLocked,
      userLoggedIn: user && user.loggedIn,
      channelName: channel && channel.name,
      accountSubDomainName: account && account.subdomain_name,
    });

    return (
      <Wizard>
        {/* empty uploader */}
        <Step action="show_upload_files" disabled={hasFiles}>
          <Files
            transfer={transfer}
            fileCount={transfer.files && transfer.files.length}
          />
        </Step>
        {/* File list & message field */}
        <Step
          prevButton={{
            text: I18n.t('transfer.mobile.button.cancel'),
            onClick: this.cancelTransfer,
          }}
          nextButton={{
            text: I18n.t('transfer.mobile.button.next'),
          }}
          action="show_add_message"
        >
          <div className="wizard__box">
            <Files transfer={transfer} fileCount={transfer.files.length} />
          </div>
          <UploaderMessage transfer={transfer} setMessage={this.setMessage} />
        </Step>
        {/* transfer type selection */}
        <Step
          disabled={lockedRecipient}
          prevButton={{
            text: I18n.t('transfer.mobile.button.back'),
          }}
          nextButton={{
            text:
              transfer.type === 'link'
                ? I18n.t('transfer.buttons.transfer')
                : I18n.t('transfer.mobile.button.next'),
            onClick: transfer.type === 'link' ? this.startTransfer : null,
          }}
          action="show_select_type"
        >
          <TransferTypeSelector
            type={transfer.type}
            onTransferTypeEmailClick={this.setTransferTypeEmail}
            onTransferTypeLinkClick={this.setTransferTypeLink}
          />
        </Step>
        {/* email addresses */}
        <Step
          prevButton={{
            text: I18n.t('transfer.mobile.button.back'),
          }}
          nextButton={{
            text: I18n.t('transfer.buttons.transfer'),
            onClick: this.startTransfer,
          }}
        >
          <UploaderRecipients
            addRecipient={this.props.addRecipient}
            transfer={transfer}
            onSenderBlur={this.onSenderBlur}
            onSenderChange={this.onSenderChange}
            lockedRecipient={lockedRecipient}
            recipientsCount={transfer.recipients.length}
            removeRecipient={this.props.removeRecipient}
            senderEmail={this.state.senderEmail}
            lockedSender={Helper.isSenderLocked(user.loggedIn)}
          />
        </Step>
      </Wizard>
    );
  }
}

MobileUploader.propTypes = {
  account: PropTypes.object,
  addRecipient: PropTypes.func.isRequired,
  changeType: PropTypes.func.isRequired,
  channel: PropTypes.object,
  clearTransferNotification: PropTypes.func.isRequired,
  create: PropTypes.func.isRequired,
  uploadTransfer: PropTypes.func.isRequired,
  notification: PropTypes.object,
  removeRecipient: PropTypes.func.isRequired,
  session: PropTypes.object,
  setMessage: PropTypes.func.isRequired,
  setSender: PropTypes.func.isRequired,
  transfer: PropTypes.object.isRequired,
  user: PropTypes.object,
  allowedSize: PropTypes.number,
  isForwardedTransfer: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  allowedSize: allowedSizeSelector(state),
  isForwardedTransfer: getIsForwardedTransfer(state),
});

const mapDispatchToProps = {
  addRecipient: Actions.Transfer.addRecipient,
  changeType: Actions.Transfer.changeType,
  clearTransferNotification: Actions.Notification.clearTransfer,
  create: Actions.Transfer.create,
  uploadTransfer,
  removeRecipient: Actions.Transfer.removeRecipient,
  setMessage: Actions.Transfer.setMessage,
  setSender: Actions.Transfer.setSender,
};

export default connect(mapStateToProps, mapDispatchToProps)(MobileUploader);
